import React, { createContext, useContext, useState, useEffect } from 'react';
import AuthApi from './components/Services/Authapi';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const getInitialThemePreference = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  // First check localStorage for immediate availability
  const stored = window.localStorage.getItem('theme');
  if (stored) {
    return stored === 'dark';
  }
  // Check system preference
  if (window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  return false;
};

const hasUsableAuthToken = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const token = window.localStorage.getItem('authToken');
  return Boolean(token && token !== 'false' && token !== 'null' && token !== 'undefined');
};

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(getInitialThemePreference);
  const [isLoading, setIsLoading] = useState(false); // Changed to false to show initial theme immediately
  const [isSaving, setIsSaving] = useState(false);

  // Apply initial theme immediately to prevent flash
  useEffect(() => {
    // Apply the initial theme immediately to prevent flash
    const initialTheme = getInitialThemePreference();
    setDarkMode(initialTheme);
    
    // Update document immediately with initial theme
    const themeName = initialTheme ? 'dark' : 'light';
    if (typeof document !== 'undefined') {
      document.body.classList.toggle('dark-theme', initialTheme);
      document.documentElement.setAttribute('data-theme', themeName);
      document.documentElement.style.setProperty('color-scheme', themeName);
    }
    
    // Set loading to true to prevent saving to database on initial load
    setIsLoading(true);
  }, []);

  // Fetch theme preference from database on mount
  useEffect(() => {
    const fetchThemeFromDatabase = async () => {
      if (!hasUsableAuthToken()) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await AuthApi.getThemePreference();
        if (response && response.status && response.data && response.data.theme) {
          const isDark = response.data.theme === 'dark';
          setDarkMode(isDark);
          // Also update localStorage
          if (typeof window !== 'undefined') {
            window.localStorage.setItem('theme', response.data.theme);
          }
        }
      } catch (error) {
        console.error('Error fetching theme from database:', error);
        // Fall back to localStorage if API fails
      } finally {
        setIsLoading(false);
      }
    };

    fetchThemeFromDatabase();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handlePreferenceChange = (event) => {
      if (window.localStorage.getItem('theme')) {
        return;
      }
      setDarkMode(event.matches);
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handlePreferenceChange);
    } else {
      mediaQuery.addListener(handlePreferenceChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handlePreferenceChange);
      } else {
        mediaQuery.removeListener(handlePreferenceChange);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const themeName = darkMode ? 'dark' : 'light';
    document.body.classList.toggle('dark-theme', darkMode);
    document.documentElement.setAttribute('data-theme', themeName);
    document.documentElement.style.setProperty('color-scheme', themeName);
    
    // Save to localStorage
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('theme', themeName);
    }
    
    // Skip saving to database on initial load
    if (isLoading) {
      setIsLoading(false);
      return;
    }
    
    // Save to database (only when theme actually changes after initial load)
    if (!hasUsableAuthToken()) {
      return;
    }

    const saveThemeToDatabase = async () => {
      setIsSaving(true);
      try {
        const response = await AuthApi.saveThemePreference(themeName);
        if (response && response.status) {
          console.log('Theme saved to database successfully');
        }
      } catch (error) {
        console.error('Error saving theme to database:', error);
        // Keep localStorage as fallback even if API fails
      } finally {
        setIsSaving(false);
      }
    };
    
    saveThemeToDatabase();
  }, [darkMode, isLoading]);

  const toggleTheme = () => {
    if (isSaving) return; // Prevent rapid toggling while saving
    setDarkMode((prevMode) => !prevMode);
  };

  return (
    <ThemeContext.Provider value={{ darkMode, toggleTheme, isSaving }}>
      {children}
    </ThemeContext.Provider>
  );
};

export { ThemeContext };
